import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { recordsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { ArrowLeft, Loader2, Car, Truck, Bus, Package } from 'lucide-react';
import { toast } from 'sonner';

export default function RecordFormPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    record_type: 'car',
    registration_number: '',
    storage_serial_number: '',
    owner_name: '',
    owner_phone: '',
    owner_email: '',
    make: '',
    model: '',
    year: '',
    vin_number: '',
    ngv_origin: 'oem',
    conversion_kit_manufacturer: '',
    conversion_kit_model: '',
    installer_name: '',
    installer_license: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (formData.record_type === 'cng_storage') {
      if (!formData.storage_serial_number) {
        toast.error('Storage Serial Number is required for CNG Storage');
        return;
      }
    } else {
      if (!formData.registration_number) {
        toast.error('Registration Number is required');
        return;
      }
    }

    setLoading(true);
    try {
      const payload = { ...formData };
      if (formData.year) payload.year = parseInt(formData.year);
      
      // Clear irrelevant fields
      if (formData.record_type === 'cng_storage') {
        delete payload.registration_number;
      } else {
        delete payload.storage_serial_number;
      }

      if (formData.ngv_origin === 'oem') {
        delete payload.conversion_kit_manufacturer;
        delete payload.conversion_kit_model;
        delete payload.installer_name;
        delete payload.installer_license;
      }

      const response = await recordsAPI.create(payload);
      toast.success('Record created successfully');
      navigate(`/records/${response.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create record');
    } finally {
      setLoading(false);
    }
  };

  const isCNGStorage = formData.record_type === 'cng_storage';
  const isRetrofitted = formData.ngv_origin === 'retrofitted';

  return (
    <div className="max-w-2xl mx-auto space-y-6 animate-fadeIn" data-testid="record-form">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/records')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="font-heading text-2xl font-bold">New Record</h1>
          <p className="text-muted-foreground">Register a new vehicle or CNG storage facility</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Record Type */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Record Type</CardTitle>
            <CardDescription>Select the type of record you're creating</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {[
                { value: 'car', label: '🚗 Light Vehicle', icon: Car },
                { value: '3w', label: '🛺 3-Wheeler', icon: Car },
                { value: 'bus', label: '🚌 Bus', icon: Bus },
                { value: 'truck', label: '🛻 Truck', icon: Truck },
                { value: 'trailer', label: '🚛 Trailer (CNG Tube)', icon: Truck },
                { value: 'cng_storage', label: '🏭 CNG Storage', icon: Package },
              ].map((type) => {
                const Icon = type.icon;
                return (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, record_type: type.value })}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      formData.record_type === type.value
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-primary/50'
                    }`}
                    data-testid={`type-${type.value}`}
                  >
                    <Icon className={`h-6 w-6 mx-auto mb-2 ${
                      formData.record_type === type.value ? 'text-primary' : 'text-muted-foreground'
                    }`} />
                    <span className={`text-sm font-medium ${
                      formData.record_type === type.value ? 'text-primary' : ''
                    }`}>{type.label}</span>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Identification */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Identification</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {isCNGStorage ? (
              <div className="space-y-2">
                <Label htmlFor="storage_serial_number">Storage Serial Number *</Label>
                <Input
                  id="storage_serial_number"
                  value={formData.storage_serial_number}
                  onChange={(e) => setFormData({ ...formData, storage_serial_number: e.target.value })}
                  placeholder="STG-2024-001"
                  className="font-mono"
                  required
                  data-testid="storage-serial"
                />
              </div>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="registration_number">Registration Number *</Label>
                <Input
                  id="registration_number"
                  value={formData.registration_number}
                  onChange={(e) => setFormData({ ...formData, registration_number: e.target.value.toUpperCase() })}
                  placeholder="T 123 ABC"
                  className="font-mono"
                  required
                  data-testid="registration-number"
                />
              </div>
            )}

            {!isCNGStorage && (
              <>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="make">Make</Label>
                    <Input
                      id="make"
                      value={formData.make}
                      onChange={(e) => setFormData({ ...formData, make: e.target.value })}
                      placeholder="Toyota"
                      data-testid="make"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="model">Model</Label>
                    <Input
                      id="model"
                      value={formData.model}
                      onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                      placeholder="Corolla"
                      data-testid="model"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="year">Year</Label>
                    <Input
                      id="year"
                      type="number"
                      min="1990"
                      max={new Date().getFullYear() + 1}
                      value={formData.year}
                      onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                      placeholder="2024"
                      data-testid="year"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="vin_number">VIN Number</Label>
                  <Input
                    id="vin_number"
                    value={formData.vin_number}
                    onChange={(e) => setFormData({ ...formData, vin_number: e.target.value.toUpperCase() })}
                    placeholder="1HGBH41JXMN109186"
                    className="font-mono"
                    data-testid="vin"
                  />
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Owner Information */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Owner Information</CardTitle>
            <CardDescription>Contact details for the owner</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="owner_name">Owner Name</Label>
              <Input
                id="owner_name"
                value={formData.owner_name}
                onChange={(e) => setFormData({ ...formData, owner_name: e.target.value })}
                placeholder="John Mwamba"
                data-testid="owner-name"
              />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="owner_phone">Phone</Label>
                <Input
                  id="owner_phone"
                  type="tel"
                  value={formData.owner_phone}
                  onChange={(e) => setFormData({ ...formData, owner_phone: e.target.value })}
                  placeholder="+255 123 456 789"
                  data-testid="owner-phone"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="owner_email">Email</Label>
                <Input
                  id="owner_email"
                  type="email"
                  value={formData.owner_email}
                  onChange={(e) => setFormData({ ...formData, owner_email: e.target.value })}
                  placeholder="owner@example.com"
                  data-testid="owner-email"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* NGV Origin */}
        {!isCNGStorage && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">NGV Origin</CardTitle>
              <CardDescription>How was the CNG system installed?</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <RadioGroup
                value={formData.ngv_origin}
                onValueChange={(value) => setFormData({ ...formData, ngv_origin: value })}
                className="flex gap-4"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="oem" id="oem" />
                  <Label htmlFor="oem" className="cursor-pointer">OEM (Factory Installed)</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="retrofitted" id="retrofitted" />
                  <Label htmlFor="retrofitted" className="cursor-pointer">Retrofitted</Label>
                </div>
              </RadioGroup>

              {isRetrofitted && (
                <div className="pt-4 space-y-4 border-t">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="conversion_kit_manufacturer">Kit Manufacturer</Label>
                      <Input
                        id="conversion_kit_manufacturer"
                        value={formData.conversion_kit_manufacturer}
                        onChange={(e) => setFormData({ ...formData, conversion_kit_manufacturer: e.target.value })}
                        placeholder="Tomasetto"
                        data-testid="kit-manufacturer"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="conversion_kit_model">Kit Model</Label>
                      <Input
                        id="conversion_kit_model"
                        value={formData.conversion_kit_model}
                        onChange={(e) => setFormData({ ...formData, conversion_kit_model: e.target.value })}
                        placeholder="AT09"
                        data-testid="kit-model"
                      />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="installer_name">Installer Name/Company</Label>
                      <Input
                        id="installer_name"
                        value={formData.installer_name}
                        onChange={(e) => setFormData({ ...formData, installer_name: e.target.value })}
                        placeholder="CNG Solutions Ltd"
                        data-testid="installer-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="installer_license">Installer License</Label>
                      <Input
                        id="installer_license"
                        value={formData.installer_license}
                        onChange={(e) => setFormData({ ...formData, installer_license: e.target.value })}
                        placeholder="INS-2024-001"
                        className="font-mono"
                        data-testid="installer-license"
                      />
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Actions */}
        <div className="flex gap-4">
          <Button type="button" variant="outline" onClick={() => navigate('/records')}>
            Cancel
          </Button>
          <Button type="submit" disabled={loading} data-testid="submit-btn">
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              'Create Record'
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
